import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ThumbsUp, Plus, MagnifyingGlass, Funnel, Gauge, Package, ArrowUp } from '@phosphor-icons/react';
import { DepartmentNav } from '../components/DepartmentNav';
import { UpsellStrip } from '../components/UpsellStrip';
import { PageWhy } from '../components/PageWhy';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

const PRODUCT_TYPES = [
  { value: 'fertilizer', label: 'Fertilizer', icon: '🌱' },
  { value: 'pre-emergent', label: 'Pre-Emergent', icon: '🛡️' },
  { value: 'granular_insecticide', label: 'Granular Insecticide', icon: '🐛' },
  { value: 'seed', label: 'Grass Seed', icon: '🌾' },
  { value: 'lime', label: 'Lime', icon: '�ite' },
  { value: 'other', label: 'Other', icon: '📦' }
];

const POPULAR_SPREADERS = [
  'Scotts Elite Spreader',
  'Scotts EdgeGuard DLX',
  'Scotts Wizz Hand-Held',
  'Earthway 2150 Commercial',
  'Echo RB-60',
  'Lesco 101186 High Wheel',
  'Spyker Ergo-Pro',
  'Chapin 82088B',
  'Agri-Fab 45-0462'
];

export const SpreaderSettingsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);
  const [settings, setSettings] = useState([]);
  const [filteredSettings, setFilteredSettings] = useState([]);
  const [mySpreaderData, setMySpreaderData] = useState({ spreaders: [], settings: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const [newSetting, setNewSetting] = useState({
    spreader_brand: '',
    spreader_model: '',
    product_name: '',
    product_type: 'fertilizer',
    setting_value: '',
    rate_per_1000_sqft: '',
    notes: ''
  });

  useEffect(() => {
    fetchSettings();
    if (user) {
      fetchMySpreaderSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    filterSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, searchQuery, filterBrand, filterType]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/spreader-settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch spreader settings:', error);
      toast.error('Failed to load spreader settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchMySpreaderSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my-spreader-settings`, { withCredentials: true });
      setMySpreaderData(res.data);
    } catch (error) {
      console.error('Failed to fetch my spreader settings:', error);
    }
  };

  const filterSettings = () => {
    let filtered = [...settings];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.spreader_brand.toLowerCase().includes(query) ||
        s.spreader_model.toLowerCase().includes(query) ||
        s.product_name.toLowerCase().includes(query)
      );
    }

    if (filterBrand !== 'all') {
      filtered = filtered.filter(s => s.spreader_brand.toLowerCase().includes(filterBrand.toLowerCase()));
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(s => s.product_type === filterType);
    }

    // Sort by upvotes
    filtered.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
    
    setFilteredSettings(filtered);
  };

  const handleAddSetting = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API_URL}/api/spreader-settings`, newSetting, { withCredentials: true });
      setSettings([res.data, ...settings]);
      setShowAddDialog(false);
      setNewSetting({
        spreader_brand: '',
        spreader_model: '',
        product_name: '',
        product_type: 'fertilizer',
        setting_value: '',
        rate_per_1000_sqft: '',
        notes: ''
      });
      toast.success('Spreader setting added! Thank you for contributing.');
    } catch (error) {
      toast.error('Failed to add spreader setting');
    }
  };

  const handleUpvote = async (settingId) => {
    if (!user) {
      toast.error('Please log in to upvote');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/spreader-settings/${settingId}/upvote`, {}, { withCredentials: true });
      setSettings(settings.map(s => 
        s.id === settingId ? { ...s, upvotes: (s.upvotes || 0) + 1 } : s
      ));
      toast.success('Thanks for your feedback!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You already upvoted this setting');
      } else {
        toast.error('Failed to upvote');
      }
    }
  };

  const getUniqueBrands = () => {
    const brands = new Set(settings.map(s => s.spreader_brand));
    return Array.from(brands).sort();
  };

  const getProductTypeIcon = (type) => {
    const found = PRODUCT_TYPES.find(t => t.value === type);
    return found ? found.icon : '📦';
  };

  const getProductTypeLabel = (type) => {
    const found = PRODUCT_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  return (
    <div data-testid="spreader-settings-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Gauge weight="duotone" size={36} className="text-primary" />
              Spreader Settings Library
            </h1>
            <p className="text-muted-foreground mt-1">
              Community-contributed spreader settings for common lawn care products
            </p>
            <PageWhy testId="spreader-page-why">Find the exact setting for your spreader and your product — so you apply the right rate the first time, no calibration guesswork.</PageWhy>
          </div>

          {user && (
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-setting-btn">
                  <Plus weight="bold" size={18} className="mr-2" />
                  Contribute Setting
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Share a Spreader Setting</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddSetting} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Spreader Brand *</Label>
                      <Input
                        data-testid="spreader-brand-input"
                        value={newSetting.spreader_brand}
                        onChange={(e) => setNewSetting({ ...newSetting, spreader_brand: e.target.value })}
                        placeholder="e.g., Scotts, Earthway"
                        required
                        list="spreader-brands"
                      />
                      <datalist id="spreader-brands">
                        {['Scotts', 'Earthway', 'Echo', 'Lesco', 'Spyker', 'Chapin', 'Agri-Fab'].map(brand => (
                          <option key={brand} value={brand} />
                        ))}
                      </datalist>
                    </div>
                    <div>
                      <Label>Spreader Model *</Label>
                      <Input
                        data-testid="spreader-model-input"
                        value={newSetting.spreader_model}
                        onChange={(e) => setNewSetting({ ...newSetting, spreader_model: e.target.value })}
                        placeholder="e.g., Elite, EdgeGuard DLX"
                        required
                        list="spreader-models"
                      />
                      <datalist id="spreader-models">
                        {POPULAR_SPREADERS.map(model => (
                          <option key={model} value={model} />
                        ))}
                      </datalist>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Product Name *</Label>
                    <Input
                      data-testid="product-name-input"
                      value={newSetting.product_name}
                      onChange={(e) => setNewSetting({ ...newSetting, product_name: e.target.value })}
                      placeholder="e.g., Scotts Turf Builder, Milorganite"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Product Type *</Label>
                      <Select
                        value={newSetting.product_type}
                        onValueChange={(val) => setNewSetting({ ...newSetting, product_type: val })}
                      >
                        <SelectTrigger data-testid="product-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCT_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Setting Value *</Label>
                      <Input
                        data-testid="setting-value-input"
                        value={newSetting.setting_value}
                        onChange={(e) => setNewSetting({ ...newSetting, setting_value: e.target.value })}
                        placeholder="e.g., 5, D, 12"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label>Rate per 1,000 sq ft</Label>
                    <Input
                      value={newSetting.rate_per_1000_sqft}
                      onChange={(e) => setNewSetting({ ...newSetting, rate_per_1000_sqft: e.target.value })}
                      placeholder="e.g., 3 lbs, 4 oz"
                    />
                  </div>
                  
                  <div>
                    <Label>Notes / Tips</Label>
                    <Input
                      value={newSetting.notes}
                      onChange={(e) => setNewSetting({ ...newSetting, notes: e.target.value })}
                      placeholder="e.g., Walk at normal pace, two passes recommended"
                    />
                  </div>
                  
                  <Button type="submit" className="w-full">Submit Setting</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="mb-6">
          <UpsellStrip
            feature="spreader_settings"
            headline="Never calibrate in the driveway again."
            body="Homeowner Seasonal unlocks the full settings library, matched to your specific spreader and product — plus community-voted tips."
            ctaLabel="Unlock Full Library"
            testId="spreader-upsell-strip"
          />
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  data-testid="search-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by spreader or product name..."
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Select value={filterBrand} onValueChange={setFilterBrand}>
                  <SelectTrigger data-testid="filter-brand-select" className="w-40">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {getUniqueBrands().map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger data-testid="filter-type-select" className="w-44">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Product Types</SelectItem>
                    {PRODUCT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for My Spreaders vs All */}
        {user && mySpreaderData.spreaders.length > 0 && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
            <TabsList>
              <TabsTrigger value="my-spreaders" className="relative">
                My Spreaders
                <span className="ml-2 bg-primary text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                  {mySpreaderData.spreaders.length}
                </span>
              </TabsTrigger>
              <TabsTrigger value="all">All Settings</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* My Spreaders Content */}
        {activeTab === 'my-spreaders' && user && mySpreaderData.spreaders.length > 0 ? (
          <div>
            {/* My Spreaders List */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Your Spreaders</h3>
              <div className="flex flex-wrap gap-2">
                {mySpreaderData.spreaders.map(spreader => (
                  <div
                    key={spreader.id}
                    className="px-4 py-2 bg-primary/10 border border-primary/20 rounded-full flex items-center gap-2"
                  >
                    <span>🌱</span>
                    <span className="font-medium">{spreader.name}</span>
                    {spreader.brand && (
                      <span className="text-sm text-muted-foreground">({spreader.brand})</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Matched Settings */}
            {mySpreaderData.settings.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Gauge size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No Matching Settings Found</h3>
                  <p className="text-muted-foreground mb-4">
                    We couldn't find settings for your spreader brand(s). Be the first to contribute!
                  </p>
                  <Button onClick={() => setShowAddDialog(true)}>
                    <Plus weight="bold" size={18} className="mr-2" />
                    Add Setting for Your Spreader
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-3">
                  Settings for Your Spreaders ({mySpreaderData.settings.length})
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {mySpreaderData.settings.map((setting) => (
                    <Card key={setting.id} className="hover:shadow-md transition-shadow border-primary/20">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                              <span className="text-xl">{getProductTypeIcon(setting.product_type)}</span>
                              {setting.product_name}
                            </CardTitle>
                            <CardDescription>
                              {setting.spreader_brand} {setting.spreader_model}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleUpvote(setting.id)}
                              className="text-muted-foreground hover:text-primary"
                            >
                              <ArrowUp size={16} />
                              <span className="ml-1">{setting.upvotes || 0}</span>
                            </Button>
                          </div>
                        </div>
                        {setting.matched_equipment_name && (
                          <p className="text-xs text-primary mt-1">
                            Matches: {setting.matched_equipment_name}
                          </p>
                        )}
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="bg-primary/10 rounded-lg p-4 text-center">
                            <p className="text-sm text-muted-foreground">Spreader Setting</p>
                            <p className="text-4xl font-bold text-primary">{setting.setting_value}</p>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="bg-secondary/50 rounded p-2">
                              <p className="text-muted-foreground text-xs">Product Type</p>
                              <p className="font-medium">{getProductTypeLabel(setting.product_type)}</p>
                            </div>
                            {setting.rate_per_1000_sqft && (
                              <div className="bg-secondary/50 rounded p-2">
                                <p className="text-muted-foreground text-xs">Rate/1000 sq ft</p>
                                <p className="font-medium">{setting.rate_per_1000_sqft}</p>
                              </div>
                            )}
                          </div>
                          
                          {setting.notes && (
                            <div className="text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-yellow-800">💡 {setting.notes}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          /* All Settings Results */
          <>
        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : filteredSettings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Gauge size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No Settings Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || filterBrand !== 'all' || filterType !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Be the first to contribute a spreader setting!'}
              </p>
              {user && (
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus weight="bold" size={18} className="mr-2" />
                  Contribute Setting
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredSettings.map((setting) => (
              <Card key={setting.id} data-testid={`setting-card-${setting.id}`} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <span className="text-xl">{getProductTypeIcon(setting.product_type)}</span>
                        {setting.product_name}
                      </CardTitle>
                      <CardDescription>
                        {setting.spreader_brand} {setting.spreader_model}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUpvote(setting.id)}
                        className="text-muted-foreground hover:text-primary"
                        data-testid={`upvote-btn-${setting.id}`}
                      >
                        <ArrowUp size={16} />
                        <span className="ml-1">{setting.upvotes || 0}</span>
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Setting Value - Large and prominent */}
                    <div className="bg-primary/10 rounded-lg p-4 text-center">
                      <p className="text-sm text-muted-foreground">Spreader Setting</p>
                      <p className="text-4xl font-bold text-primary">{setting.setting_value}</p>
                    </div>
                    
                    {/* Details */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-secondary/50 rounded p-2">
                        <p className="text-muted-foreground text-xs">Product Type</p>
                        <p className="font-medium">{getProductTypeLabel(setting.product_type)}</p>
                      </div>
                      {setting.rate_per_1000_sqft && (
                        <div className="bg-secondary/50 rounded p-2">
                          <p className="text-muted-foreground text-xs">Rate/1000 sq ft</p>
                          <p className="font-medium">{setting.rate_per_1000_sqft}</p>
                        </div>
                      )}
                    </div>
                    
                    {setting.notes && (
                      <div className="text-sm p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-yellow-800">💡 {setting.notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </>
        )}

        {/* Tips Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Spreader Calibration Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold mb-2">📏 Why Calibrate?</h4>
                <p className="text-sm text-muted-foreground">
                  Every spreader and walking pace is different. Calibrating ensures you apply the right amount of product - not too much (burn risk) and not too little (ineffective).
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">🎯 How to Calibrate</h4>
                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Measure out 1,000 sq ft on your driveway or lawn</li>
                  <li>Weigh product before and after spreading</li>
                  <li>Adjust setting until you hit target rate</li>
                  <li>Share your results with the community!</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
